/** Provides the reusable modal workflow for boosting one or many brand items. */
import * as React from "react";
import {useEffect, useMemo, useState} from "react";
import {
   Alert,
   Box,
   Button,
   Chip,
   Dialog,
   DialogActions,
   DialogContent,
   DialogTitle,
   LinearProgress,
   Stack,
   Typography
} from "@mui/material";
import {FormProvider, useForm} from "react-hook-form";
import {
   BrandItemBoostItemsQuery,
   useBrandItemBoostCancelMutation,
   useBrandItemBoostItemsQuery,
   useBrandItemBoostSetMutation
} from "../../Queries";
import {FormInputCheckbox} from "../shop/wholesale/form/FormInputCheckbox";
import {FormInputDateTime} from "../shop/wholesale/form/FormInputDateTime";
import {FormInputPercent} from "../shop/wholesale/form/FormInputPercent";
import {Thumbnail} from "../../components/Thumbnail";
import {BrandItemBoostChip} from "./BrandItemBoostChip";
import {
   BoostFormValues,
   boostedPrice,
   boostRefusalReason,
   countRunningBoosts,
   isBoostRunning,
   toWholePercent,
   validateBoostForm
} from "./boostRules";
import {
   BOOST_DATE_TIME_FORMAT,
   boostDurationOptions,
   defaultBoostFormValues
} from "./BrandItemBoostFormOptions";

type BoostItem = NonNullable<NonNullable<BrandItemBoostItemsQuery['BrandItems']>[number]>

type BrandItemBoostDialogProps = {
   /** One id from the item page or the dashboard, many from a list selection. */
   brandItemIds: string[]
   open: boolean
   onClose: () => void
   /** Fired once at least one boost changed, so the caller can refetch. */
   onCompleted?: () => void
}

/** The message Core sent back, or a usable fallback. */
const errorMessage = (e: Error): string => e.message || 'The boost could not be set';

/**
 * Sets, replaces or ends a time-limited boost on one or more stock items.
 *
 * Takes item ids and nothing else - it loads what it needs itself - so the
 * item page, the stock list and the dashboard can all open the same dialog
 * without having a BrandItem in hand.
 */
export const BrandItemBoostDialog = ({brandItemIds, open, onClose, onCompleted}: BrandItemBoostDialogProps) => {
   const {data, loading} = useBrandItemBoostItemsQuery({
      variables: {
         brandItemIds
      },
      skip: !open || !brandItemIds.length,
      fetchPolicy: 'network-only'
   });

   const [boostSet] = useBrandItemBoostSetMutation();
   const [boostCancel] = useBrandItemBoostCancelMutation();

   const [progress, setProgress] = useState<number | undefined>();
   const [failures, setFailures] = useState<string[]>([]);

   const items = useMemo(() => {
      return (data?.BrandItems || []).filter((item): item is BoostItem => !!item);
   }, [data]);

   const single = items.length === 1 ? items[0] : undefined;
   const runningBoost = isBoostRunning(single?.activeBoost) ? single?.activeBoost : undefined;
   const durations = useMemo(() => boostDurationOptions(), [open]);

   const form = useForm<BoostFormValues>({
      defaultValues: defaultBoostFormValues()
   });
   const values = form.watch();

   useEffect(() => {
      if (open && data) {
         form.reset(defaultBoostFormValues(items.length === 1 ? items[0] : undefined));
         setFailures([]);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [open, data]);

   const validation = validateBoostForm(values);
   const busy = progress !== undefined;

   const alreadyBoosted = countRunningBoosts(items);
   const refused = items.filter(item => boostRefusalReason(item));

   const onSetBoost = async (formValues: BoostFormValues) => {
      const pricePct = formValues.discountEnabled ? toWholePercent(formValues.pricePct) : undefined;
      const collected: string[] = [];

      setFailures([]);
      for (const [index, item] of items.entries()) {
         setProgress(index);
         try {
            await boostSet({
               variables: {
                  brandItemId: item._id,
                  endTs: new Date(formValues.endTs!),
                  pricePct,
                  visibility: formValues.visibility
               }
            });
         } catch (e) {
            collected.push(`${item.itemNr}: ${errorMessage(e as Error)}`);
         }
      }
      setProgress(undefined);

      if (collected.length < items.length) {
         onCompleted?.();
      }
      if (collected.length) {
         setFailures(collected);
      } else {
         onClose();
      }
   }

   const onRemoveBoost = async () => {
      if (!single) {
         return;
      }
      setProgress(0);
      try {
         await boostCancel({
            variables: {
               brandItemId: single._id
            }
         });
         setProgress(undefined);
         onCompleted?.();
         onClose();
      } catch (e) {
         setProgress(undefined);
         setFailures([errorMessage(e as Error)]);
      }
   }

   const title = single
      ? `Boost item ${single.itemNr}`
      : `Boost ${brandItemIds.length} items`;

   return <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth={"sm"} fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
         {loading && <LinearProgress/>}

         {single && <Stack direction={"row"} spacing={2} alignItems={"center"} sx={{mb: 2}}>
            <Thumbnail size={56} image={single.imageUrls?.[0]}/>
            <Box>
               {runningBoost
                  ? <BrandItemBoostChip boost={runningBoost}/>
                  : <Typography variant={"body2"} color={"text.secondary"}>No boost running</Typography>}
            </Box>
         </Stack>}

         {runningBoost && <Alert severity={"info"} sx={{mb: 2}}>
            Setting a boost replaces the one running now.
         </Alert>}

         {!single && alreadyBoosted > 0 && <Alert severity={"info"} sx={{mb: 2}}>
            {alreadyBoosted} of {items.length} already have a boost running. Setting a new one replaces it.
         </Alert>}

         {refused.length > 0 && <Alert severity={"warning"} sx={{mb: 2}}>
            {single
               ? boostRefusalReason(single)
               : `${refused.length} of ${items.length} cannot be boosted - only READY, unsold, non-discarded items can.`}
         </Alert>}

         <FormProvider {...form}>
            <form onSubmit={form.handleSubmit(onSetBoost)}>
               <FormInputCheckbox name={"discountEnabled"} label={"Discount the price"}/>
               {values.discountEnabled && <>
                   <FormInputPercent name={"pricePct"} label={"Discount"}/>
                  {single && <PricePreview item={single} pricePct={toWholePercent(values.pricePct)}/>}
               </>}

               <FormInputCheckbox name={"visibility"}
                                  label={"Boost visibility - rank ahead of unboosted items"}/>

               <Typography variant={"body2"} sx={{mt: 2, mb: 1}}>Boost ends</Typography>
               <Stack direction={"row"} spacing={1} sx={{mb: 1}}>
                  {durations.map(duration => <Chip
                     key={duration.label}
                     label={duration.label}
                     size={"small"}
                     variant={"outlined"}
                     onClick={() => form.setValue('endTs', duration.endTs.format(BOOST_DATE_TIME_FORMAT))}
                  />)}
               </Stack>
               <FormInputDateTime name={"endTs"} label={"Ends"}/>
            </form>
         </FormProvider>

         {busy && <Box sx={{mt: 2}}>
            <LinearProgress/>
            <Typography variant={"body2"} color={"text.secondary"}>
               Boosting {Math.min((progress || 0) + 1, items.length)} of {items.length}
            </Typography>
         </Box>}

         {failures.length > 0 && <Alert severity={"error"} sx={{mt: 2}}>
            {failures.length === items.length
               ? 'No items were boosted.'
               : `${items.length - failures.length} boosted, ${failures.length} failed.`}
            {failures.map(failure => <div key={failure}>{failure}</div>)}
         </Alert>}
      </DialogContent>
      <DialogActions>
         <Button onClick={onClose} disabled={busy}>Cancel</Button>
         {runningBoost && <Button onClick={onRemoveBoost} disabled={busy} color={"secondary"}>
             Remove boost
         </Button>}
         <Button variant={"contained"}
                 disabled={busy || !!validation || !items.length}
                 title={validation}
                 onClick={form.handleSubmit(onSetBoost)}>
            Set boost
         </Button>
      </DialogActions>
   </Dialog>
}

type PricePreviewProps = {
   item: BoostItem
   pricePct: number | null
}

/**
 * What the item's price becomes once the boost runs. An estimate: Core takes
 * the lower of the campaign price and the boosted price, so a running campaign
 * can land it lower still.
 */
const PricePreview = ({item, pricePct}: PricePreviewProps) => {
   if (!pricePct) {
      return null;
   }
   const prices = (item.sellingPrices || []).filter(price => !!price);
   if (!prices.length) {
      return null;
   }

   return <Box sx={{mb: 1}}>
      {prices.map(price => <Typography key={price!.currency} variant={"body2"} color={"text.secondary"}>
         {price!.value} → ≈{boostedPrice(price!.value, pricePct)} {price!.currency}
      </Typography>)}
   </Box>
}
